import React, { useEffect, useState } from "react";
import {
  Create,
  DateField,
  SimpleForm,
  List,
  Datagrid,
  TextField,
  TextInput,
  ReferenceManyField,
  SelectInput,
  SingleFieldList,
  ChipField,
  required,
  Show,
  usePermissions,
  NumberInput,
  Button,
  useListContext,
  useShowController,
  TabbedShowLayoutTabs,
  TabbedShowLayout,
  Tab,
  useRedirect,
  Toolbar,
  SaveButton,
  DateInput,
  useUpdate,
  useGetManyReference,
  FunctionField,
  useRefresh,
  useNotify,
  ReferenceInput,
  AutocompleteInput,
  ReferenceField,
  useRecordContext,
  useGetMany,
} from "react-admin";
import {
  Box,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Typography,
  Grid,
  InputLabel,
  Chip,
  TextField as MuiText,
  DialogContent,
  DialogActions,
  Checkbox,
  Dialog,
  DialogTitle,
  IconButton,
  List as MuiList,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { styled, useTheme } from "@mui/system";
import { BillingAccountCreateDoc } from "../documentation/CreateBillingAccountDoc";
import { permissionCheck, renderIfPermissions } from "../api/permissionManager";
import { useNavigate, useParams } from "react-router-dom";
import {
  Add,
  CheckBox,
  CheckBoxOutlineBlank,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  TrendingFlat,
  Close,
} from "@mui/icons-material";
import EmptyComponent from "../components/EmptyComponent";
import CustomAccountContractCreateDoc from "../documentation/CreateCustomAccountContractDoc";
import CustomDateInput from "../components/CustomDateInput";
import { IALoading } from "../components/IAProgress.js";

const CancelButton = styled(Button)(({ theme }) => ({
  "&.MuiButton-outlinedSecondary": {
    border: "1px solid gray",
    color: "gray",
    paddingLeft: "1.5em",
    paddingRight: "1.5em",
    paddingTop: "0.6em",
    paddingBottom: "0.6em",
    margin: "2em",
  },
}));

const ChangeTypography = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  color: theme.palette.text.secondary,
  "& svg": {
    margin: theme.spacing(0, 1),
    color: theme.palette.primary.main,
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(2),
    maxWidth: 600,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  padding: theme.spacing(1, 3),
}));

const BillingAccountShowActions = ({ id, name }) => {
  const { permissions } = usePermissions();
  const navigate = useNavigate();

  return (
    <Box sx={{ padding: "1em", mt: 2, display: "flex", justifyContent: "flex-start", backgroundColor: "#f5f5f5" }}>
      {renderIfPermissions(
        permissions,
        "billing",
        "write",
        <Button
          variant="contained"
          startIcon={<Add />}
          label="Add"
          onClick={() => navigate(`/billing_contract/${id}`, { state: { id, name } })}
        />
      )}
    </Box>
  );
};

export const BillingAccountFilter = [<TextInput label="Search by name" source="name" alwaysOn />];

export const BillingAccountList = () => {
  return (
    <List pagination={false} exporter={false} filters={BillingAccountFilter}>
      <Datagrid rowClick="show" size="medium" bulkActionButtons={false}>
        <TextField source="name" sortable={false} />
        <TextField source="billing_account_type" label="Type" sortable={false} />
        <DateField source="contract_anniversary" sortable={false} options={{ timeZone: "UTC" }} />
        <DateField source="valid_from" sortable={false} options={{ timeZone: "UTC" }} />
        <TextField source="price_sheet_code" sortable={false} />
      </Datagrid>
    </List>
  );
};

// Helper function to format UTC date
const formatDateToMMDDYYYY = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
};

const AccountContractUpdate = ({
  id,
  contract,
  contractList,
  handleSetActiveContracts,
  setEditingContractId,
  editingContractId,
  editActive,
  setEditActive,
  editValidFrom,
  setEditValidFrom,
  editValidTo,
  setEditValidTo,
  children,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isPending }] = useUpdate();
  const isEditing = editingContractId === id;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleEditToggle = (editing) => {
    setEditingContractId(editing ? id : null);
    if (!editing) {
      setEditActive(contract.active);
      setEditValidFrom(contract.valid_from ? new Date(contract.valid_from).toISOString().slice(0, 10) : "");
      setEditValidTo(contract.valid_to ? new Date(contract.valid_to).toISOString().slice(0, 10) : "");
    }
  };

  const validateDates = () => {
    const validFromRegex = /^\d{4}-\d{2}-01$/;
    const validToRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (editValidFrom && !validFromRegex.test(editValidFrom)) {
      notify("Valid From must be in YYYY-MM-01 format.", { type: "error" });
      return false;
    }
    if (editValidTo && !validToRegex.test(editValidTo)) {
      notify("Valid To must be in YYYY-MM-DD format.", { type: "error" });
      return false;
    }
    if (editValidTo) {
      const [year, month, day] = editValidTo.split("-").map(Number);
      const lastDayOfMonth = new Date(year, month, 0).getDate();
      if (day !== lastDayOfMonth) {
        notify(
          `Valid To must be the last day of the month (e.g., ${year}-${String(month).padStart(2, "0")}-${lastDayOfMonth}).`,
          { type: "error" }
        );
        return false;
      }
    }
    if (editValidFrom && editValidTo && new Date(editValidFrom) >= new Date(editValidTo)) {
      notify("Valid From must be before Valid To.", { type: "error" });
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validateDates()) return;
    setConfirmDialogOpen(true);
  };

  const handleConfirmSave = () => {
    const updatedContract = {
      active: editActive,
      valid_from: editValidFrom || new Date().toISOString().slice(0, 10),
      valid_to: editValidTo || null,
    };

    update(
      "account_contracts",
      { id: id, data: updatedContract },
      {
        onSuccess: () => {
          refresh();
          notify(`Account contract ${id} successfully updated`, { type: "success" });
          handleSetActiveContracts([...contractList.filter((c) => c.active && c.id !== id), updatedContract]);
          handleEditToggle(false);
          setConfirmDialogOpen(false);
        },
        onError: (error) => {
          notify(`Error updating account contract: ${JSON.stringify(error)}`, { type: "error" });
        },
      }
    );
  };

  const handleCancel = () => {
    handleEditToggle(false);
    setConfirmDialogOpen(false);
  };

  const renderActions = () =>
    isEditing ? (
      <Box sx={{ display: "flex", gap: 1 }}>
        <IconButton color="primary" size="small" onClick={handleSave} disabled={isPending}>
          <SaveIcon />
        </IconButton>
        <IconButton color="primary" size="small" onClick={handleCancel} disabled={isPending}>
          <CancelIcon />
        </IconButton>
      </Box>
    ) : (
      <IconButton color="primary" size="small" onClick={() => handleEditToggle(true)}>
        <EditIcon />
      </IconButton>
    );

  const renderActive = () =>
    isEditing ? (
      <Checkbox
        checked={editActive}
        onChange={() => setEditActive(!editActive)}
        icon={<CheckBoxOutlineBlank />}
        checkedIcon={<CheckBox />}
        disabled={isPending}
        sx={{ bgcolor: "action.selected" }}
      />
    ) : (
      <Checkbox checked={contract.active} disabled icon={<CheckBoxOutlineBlank />} checkedIcon={<CheckBox />} />
    );

  const renderValidFrom = () =>
    isEditing ? (
      <MuiText
        value={editValidFrom}
        onChange={(e) => setEditValidFrom(e.target.value)}
        variant="outlined"
        size="small"
        sx={{ bgcolor: "action.selected", width: "120px" }}
      />
    ) : (
      <span>{formatDateToMMDDYYYY(contract.valid_from)}</span>
    );

  const renderValidTo = () =>
    isEditing ? (
      <MuiText
        value={editValidTo}
        onChange={(e) => setEditValidTo(e.target.value)}
        variant="outlined"
        size="small"
        sx={{ bgcolor: "action.selected", width: "120px" }}
      />
    ) : (
      <span>{formatDateToMMDDYYYY(contract.valid_to)}</span>
    );
  const confirmationDialog = () => {
    const changes = [];

    if (editActive !== contract.active) {
      changes.push(
        <ChangeTypography variant="body2" key="active">
          Active: {`${contract.active}`} <TrendingFlat /> {`${editActive}`}
        </ChangeTypography>
      );
    }

    const originalValidFrom = contract.valid_from ? new Date(contract.valid_from).toISOString().slice(0, 10) : "-";
    const newValidFrom = editValidFrom || "-";
    if (newValidFrom !== originalValidFrom) {
      changes.push(
        <ChangeTypography variant="body2" key="validFrom">
          Valid From: {originalValidFrom} <TrendingFlat /> {newValidFrom}
        </ChangeTypography>
      );
    }

    const originalValidTo = contract.valid_to ? new Date(contract.valid_to).toISOString().slice(0, 10) : "-";
    const newValidTo = editValidTo || "-";
    if (newValidTo !== originalValidTo) {
      changes.push(
        <ChangeTypography variant="body2" key="validTo">
          Valid To: {originalValidTo} <TrendingFlat /> {newValidTo}
        </ChangeTypography>
      );
    }

    return (
      <StyledDialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Confirm Changes
          <IconButton onClick={() => setConfirmDialogOpen(false)} sx={{ color: (theme) => theme.palette.grey }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent id="confirm-dialog-content">
          <Box sx={{ minWidth: 400, py: 2 }}>
            {changes.length > 0 ? (
              <>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "medium", color: "text.primary" }}>
                  You are about to make the following changes:
                </Typography>
                <MuiList dense sx={{ bgcolor: "background.default", borderRadius: 1, p: 1 }}>
                  {changes.map((change, index) => (
                    <ListItem key={index} sx={{ py: 1 }}>
                      <ListItemText primary={change} />
                    </ListItem>
                  ))}
                </MuiList>
              </>
            ) : (
              <Typography variant="subtitle1" color="text.secondary">
                No changes detected.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, justifyContent: "flex-end", gap: 1 }}>
          {changes.length > 0 && (
            <>
              <StyledButton
                id="cancel-btn"
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                label="Cancel"
              />
              <StyledButton
                id="confirm-btn"
                variant="contained"
                color="primary"
                onClick={handleConfirmSave}
                disabled={isPending}
                label="Confirm"
              />
            </>
          )}
        </DialogActions>
      </StyledDialog>
    );
  };

  return children({
    actions: renderActions,
    active: renderActive,
    valid_from: renderValidFrom,
    valid_to: renderValidTo,
    confirmationDialog,
  });
};

const checkAccountContractOverlaps = (contracts) => {
  const overlaps = [];
  const activeContracts = contracts.filter((contract) => contract.active);
  for (let i = 0; i < activeContracts.length; i++) {
    for (let j = i + 1; j < activeContracts.length; j++) {
      const contract1 = activeContracts[i];
      const contract2 = activeContracts[j];

      const validFrom1 = new Date(contract1.valid_from);
      const validFrom2 = new Date(contract2.valid_from);
      const validTo1 = contract1.valid_to ? new Date(contract1.valid_to) : null;
      const validTo2 = contract2.valid_to ? new Date(contract2.valid_to) : null;

      // Check if contract2 starts before contract1 ends (or contract1's valid_to is null)
      const contract2StartsBeforeContract1Ends = !validTo1 || validFrom2 <= validTo1;
      // Check if contract1 starts before contract2 ends (or contract2's valid_to is null)
      const contract1StartsBeforeContract2Ends = !validTo2 || validFrom1 <= validTo2;
      // Overlap exists if both conditions are true
      const hasOverlap = contract2StartsBeforeContract1Ends && contract1StartsBeforeContract2Ends;

      if (hasOverlap) {
        overlaps.push({
          contract1: contract1.id,
          contract2: contract2.id,
          structure1: contract1.contract_structure_code,
          structure2: contract2.contract_structure_code,
        });
      }
    }
  }
  return overlaps;
};

export const BillingAccountShow = (props) => {
  const { id } = useParams();
  const { permissions } = usePermissions();
  const theme = useTheme();
  const navigate = useNavigate();
  const redirect = useRedirect();
  const { record } = useShowController();
  const [acctType, setAcctType] = useState();
  const [acctName, setAcctName] = useState();
  const [contracts, setContracts] = useState([]);
  const [alertSeverity, setAlertSeverity] = useState(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [activeContracts, setActiveContracts] = useState([]);
  const [editingContractId, setEditingContractId] = useState(null);
  const [editActive, setEditActive] = useState(null);
  const [editValidFrom, setEditValidFrom] = useState(null);
  const [editValidTo, setEditValidTo] = useState(null);

  const hasBillingReadPermission = permissionCheck("billing", "read_only", permissions);
  const {
    data: contractData,
    isLoading,
    error: listError,
  } = hasBillingReadPermission
    ? useGetManyReference("account_contracts", { target: "billing_account_id", id: id })
    : { data: [], isLoading: false, error: null };

  useEffect(() => {
    if (record) {
      setAcctType(record.billing_account_type);
      setAcctName(record.name);
    }
    if (!isLoading && !listError) {
      setContracts(contractData);
      setActiveContracts(contractData.filter((contract) => contract.active));
    }
  }, [record, contractData, isLoading, listError]);

  const handleSetActiveContracts = (contracts) => {
    setActiveContracts(contracts);
  };

  useEffect(() => {
    if (!isLoading && !listError && contracts.length > 0) {
      const overlaps = checkAccountContractOverlaps(contracts);
      if (overlaps.length > 0) {
        const overlapMessages = overlaps.map((o) => `${o.structure1} and ${o.structure2}`).join(", ");
        setAlertSeverity("warning");
        setAlertMessage(`Multiple active contracts overlap: ${overlapMessages}`);
      } else {
        setAlertSeverity(null);
        setAlertMessage("");
      }
    }
  }, [contracts, isLoading, listError]);

  const handleCloseAlert = () => {
    setAlertSeverity(null);
    setAlertMessage("");
  };

  const handleCreateButtonClick = () => {
    const name = acctName;
    navigate(`/billing_contract/${record.id}`, { state: { id, name } });
  };

  const handleRedirect = (field, value) => {
    if (field === "contract_structure_code") {
      redirect("show", "contract_structures", value);
    } else if (field === "price_sheet_code") {
      redirect("show", "price_sheets", value);
    }
  };

  const handleSetEditingContractId = (id) => {
    setEditingContractId(id);
    if (id) {
      const contract = contracts.find((c) => c.id === id);
      if (contract) {
        setEditActive(contract.active);
        setEditValidFrom(contract.valid_from ? new Date(contract.valid_from).toISOString().slice(0, 10) : "");
        setEditValidTo(contract.valid_to ? new Date(contract.valid_to).toISOString().slice(0, 10) : "");
      }
    } else {
      setEditActive(null);
      setEditValidFrom(null);
      setEditValidTo(null);
    }
  };

  return (
    <Show {...props}>
      <TabbedShowLayout
        tabs={<TabbedShowLayoutTabs variant="scrollable" scrollButtons="auto" {...props} />}
        spacing={2}
        style={{ paddingBottom: "16px" }}
        syncWithLocation={false}
      >
        <Tab label="Billing Account Information" path="info">
          <Box gridColumn="span 9" sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.2)" }}>
            <Typography variant="h5" sx={{ paddingBottom: "8px" }}>
              {acctName}
            </Typography>
          </Box>
          <Grid container spacing={3} direction="column">
            <Grid item>
              <InputLabel shrink sx={{ color: "text.secondary" }}>
                Account Type
              </InputLabel>
              <TextField source="billing_account_type" label="Billing Account Type" />
            </Grid>
            <Grid item>
              <InputLabel shrink sx={{ color: "text.secondary" }}>
                External ID
              </InputLabel>
              <TextField source="external_id" label="External ID" />
            </Grid>
            <Grid item>
              <InputLabel shrink sx={{ color: "text.secondary" }}>
                Valid From
              </InputLabel>
              <DateField source="valid_from" label="Valid From" options={{ timeZone: "UTC" }} />
            </Grid>
            <Grid item>
              <InputLabel shrink sx={{ color: "text.secondary" }}>
                Contract Anniversary
              </InputLabel>
              <DateField source="contract_anniversary" label="Contract Anniversary" options={{ timeZone: "UTC" }} />
            </Grid>
            <Grid item>
              <InputLabel shrink sx={{ color: "text.secondary" }}>
                Hubspot Company ID
              </InputLabel>
              <TextField source="hubspot_company_identifier" label="Hubspot Company ID" />
            </Grid>
          </Grid>

          {renderIfPermissions(
            permissions,
            "clients",
            "read_only",
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                  <u>Associated Clients</u>
                </Typography>
                <ReferenceManyField label="Associated Clients" reference="clients" target="billing_account_id">
                  <SingleFieldList linkType="show">
                    <ChipField source="key" />
                  </SingleFieldList>
                </ReferenceManyField>
              </CardContent>
            </Card>
          )}
        </Tab>

        {renderIfPermissions(
          permissions,
          "billing",
          "read_only",
          <Tab label="Account Contracts" path="account_contracts">
            <Box width="fit-content">
              {alertSeverity && (
                <Alert severity={alertSeverity} onClose={handleCloseAlert}>
                  <AlertTitle>
                    {alertSeverity === "success" ? "Success" : alertSeverity === "warning" ? "Warning" : "Error"}
                  </AlertTitle>
                  <div data-testid="acct-contract-alert">{alertMessage}</div>
                </Alert>
              )}
            </Box>
            <ReferenceManyField reference="account_contracts" target="billing_account_id" record={record}>
              <Datagrid
                bulkActionButtons={false}
                empty={
                  <EmptyComponent
                    permissionResource="billing"
                    label={"Account Contracts"}
                    onClick={handleCreateButtonClick}
                  />
                }
                expand={<AssociatedProductsPanel />}
              >
                {renderIfPermissions(
                  permissions,
                  "billing",
                  "write",
                  <FunctionField
                    label=""
                    render={(record) => (
                      <AccountContractUpdate
                        id={record.id}
                        contract={record}
                        contractList={contracts}
                        handleSetActiveContracts={handleSetActiveContracts}
                        setEditingContractId={handleSetEditingContractId}
                        editingContractId={editingContractId}
                        editActive={editActive}
                        setEditActive={setEditActive}
                        editValidFrom={editValidFrom}
                        setEditValidFrom={setEditValidFrom}
                        editValidTo={editValidTo}
                        setEditValidTo={setEditValidTo}
                      >
                        {({ actions, confirmationDialog }) => (
                          <>
                            {actions()}
                            {confirmationDialog()}
                          </>
                        )}
                      </AccountContractUpdate>
                    )}
                  />
                )}
                {renderIfPermissions(
                  permissions,
                  "billing",
                  "write",
                  <FunctionField
                    label="Active"
                    render={(record) => (
                      <AccountContractUpdate
                        id={record.id}
                        contract={record}
                        contractList={contracts}
                        handleSetActiveContracts={handleSetActiveContracts}
                        setEditingContractId={handleSetEditingContractId}
                        editingContractId={editingContractId}
                        editActive={editActive}
                        setEditActive={setEditActive}
                        editValidFrom={editValidFrom}
                        setEditValidFrom={setEditValidFrom}
                        editValidTo={editValidTo}
                        setEditValidTo={setEditValidTo}
                      >
                        {({ active }) => active()}
                      </AccountContractUpdate>
                    )}
                  />
                )}
                <FunctionField
                  label="Contract Structure Code"
                  render={(record) => (
                    <Button
                      data-testid={`acct-contract-${record.contract_structure_code}`}
                      label={record.contract_structure_code}
                      onClick={() => handleRedirect("contract_structure_code", record.contract_structure_code)}
                      disabled={editingContractId === record.id}
                    />
                  )}
                />
                <FunctionField
                  label="Price Sheet Code"
                  render={(record) => (
                    <Button
                      label={record.price_sheet_code}
                      onClick={() => handleRedirect("price_sheet_code", record.price_sheet_code)}
                      disabled={editingContractId === record.id}
                    />
                  )}
                />
                <TextField source="created_by" />
                <DateField source="created_at" options={{ timeZone: "UTC" }} />
                <FunctionField
                  label="Valid From"
                  render={(record) =>
                    permissionCheck("billing", "write", permissions) ? (
                      <AccountContractUpdate
                        id={record.id}
                        contract={record}
                        contractList={contracts}
                        handleSetActiveContracts={handleSetActiveContracts}
                        setEditingContractId={handleSetEditingContractId}
                        editingContractId={editingContractId}
                        editActive={editActive}
                        setEditActive={setEditActive}
                        editValidFrom={editValidFrom}
                        setEditValidFrom={setEditValidFrom}
                        editValidTo={editValidTo}
                        setEditValidTo={setEditValidTo}
                      >
                        {({ valid_from }) => valid_from()}
                      </AccountContractUpdate>
                    ) : (
                      <span>{formatDateToMMDDYYYY(record.valid_from)}</span>
                    )
                  }
                />
                <FunctionField
                  label="Valid To"
                  render={(record) =>
                    permissionCheck("billing", "write", permissions) ? (
                      <AccountContractUpdate
                        id={record.id}
                        contract={record}
                        contractList={contracts}
                        handleSetActiveContracts={handleSetActiveContracts}
                        setEditingContractId={handleSetEditingContractId}
                        editingContractId={editingContractId}
                        editActive={editActive}
                        setEditActive={setEditActive}
                        editValidFrom={editValidFrom}
                        setEditValidFrom={setEditValidFrom}
                        editValidTo={editValidTo}
                        setEditValidTo={setEditValidTo}
                      >
                        {({ valid_to }) => valid_to()}
                      </AccountContractUpdate>
                    ) : (
                      <span>{formatDateToMMDDYYYY(record.valid_to)}</span>
                    )
                  }
                />
                <DateField source="contract_anniversary" options={{ timeZone: "UTC" }} />
              </Datagrid>
            </ReferenceManyField>
            {contracts.length > 0 && <BillingAccountShowActions id={id} name={acctName} acct_type={acctType} />}
          </Tab>
        )}
      </TabbedShowLayout>
    </Show>
  );
};

const CustomAccountContractCreateToolbar = ({ id }) => {
  const redirect = useRedirect();
  const handleCancel = () => {
    redirect(`/billing_accounts/${id}/show`);
  };
  return (
    <Toolbar>
      <SaveButton label="Save" />
      <CancelButton variant="outlined" onClick={handleCancel} color="secondary" label="Cancel" />
    </Toolbar>
  );
};

export const CustomAccountContractCreate = ({ id, name }) => {
  return (
    <Create
      resource="account_contracts"
      redirect={`/billing_accounts/${id}/show`}
      aside={<CustomAccountContractCreateDoc />}
      title={"Create Custom Account Contract"}
    >
      <SimpleForm toolbar={<CustomAccountContractCreateToolbar id={id} />}>
        <TextInput
          source="billing_account_external_id"
          label="Billing Account External ID"
          validate={required()}
          defaultValue={id}
          readOnly
          fullWidth
        />
        <TextInput source="name" validate={required()} defaultValue={name} readOnly fullWidth />
        <ReferenceInput source="contract_structure_code" reference="contract_structures">
          <AutocompleteInput fullWidth optionText="code" validate={required()} />
        </ReferenceInput>
        <ReferenceInput source="price_sheet_code" reference="price_sheets">
          <AutocompleteInput fullWidth optionText="code" validate={required()} />
        </ReferenceInput>
        <CustomDateInput source="valid_from" label="Valid From" validate={required()} isValidFrom />
        <CustomDateInput source="valid_to" label="Valid To" isValidTo />
        <DateInput source="contract_anniversary" validate={required()} fullWidth />
      </SimpleForm>
    </Create>
  );
};

export const BillingAccountCreate = () => {
  return (
    <Create
      resource="billing_accounts_v2"
      redirect={"/billing_accounts"}
      title="Create Billing Account"
      aside={<BillingAccountCreateDoc />}
    >
      <SimpleForm>
        <TextInput fullWidth source="name" label="Billing Account Name" validate={required()} />
        <SelectInput
          fullWidth
          source="type"
          validate={required()}
          choices={[
            { id: "house_account", name: "House Account" },
            { id: "reseller", name: "Reseller" },
          ]}
        />
        <NumberInput
          fullWidth
          source="hubspot_company_identifier"
          label="HubSpot Company Identifier"
          parse={(value) => value.toString()}
          validate={required()}
          step={1}
        />
      </SimpleForm>
    </Create>
  );
};

const AssociatedProductsList = () => {
  const { isLoading, data } = useListContext();
  const theme = useTheme();
  const redirect = useRedirect();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <IALoading size={40} />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
        No associated products.
      </Typography>
    );
  }

  return (
    <SingleFieldList linkType={false}>
      <FunctionField
        render={(productRecord) => (
          <Chip
            label={productRecord.product_code}
            onClick={() => redirect("show", "products", productRecord.product_code)}
            sx={{ cursor: "pointer", color: theme.palette.primary.main, fontSize: "0.8125rem" }}
          />
        )}
      />
    </SingleFieldList>
  );
};

const AssociatedProductsPanel = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Box sx={{ p: 2, backgroundColor: "#fafafa" }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Associated Products for Contract: {record.contract_structure_code}
      </Typography>

      <ReferenceField source="contract_structure_code" reference="contract_structures" record={record} link={false}>
        <FunctionField
          render={(structureRecord) =>
            structureRecord && (
              <ReferenceManyField
                reference="contract_structure_products"
                target="contract_structure_code"
                filter={{ id: structureRecord.code }}
              >
                <AssociatedProductsList />
              </ReferenceManyField>
            )
          }
        />
      </ReferenceField>
    </Box>
  );
};
